<p-dialog
    header="Authorize.Net Subscriptions"
    [(visible)]="visible"
    [modal]="true"
    [style]="{ width: 'min(90vw, 56rem)' }"
    [maximizable]="true"
    [draggable]="false"
    [resizable]="true"
    (onHide)="onDialogHide()">
    <div class="subscriptions-dialog">
        <h4 class="subscriptions-section-title mt-0">All profile subscriptions JSON</h4>       
        <div class="flex flex-wrap align-items-center gap-3 mb-2">
            <button
                pButton
                type="button"
                icon="pi pi-sitemap"
                label="Build all profile subscriptions JSON"
                [loading]="profileSubscriptionsExportLoading"
                (click)="buildCustomerProfileSubscriptionsJson()"></button>
            <div class="flex align-items-center gap-2">
                <p-checkbox
                    inputId="profileSubscriptionsExportIncludeTransactions"
                    [(ngModel)]="profileSubscriptionsExportIncludeTransactions"
                    [binary]="true" />
                <label for="profileSubscriptionsExportIncludeTransactions">
                    Include transactions in subscription details
                </label>
            </div>
            <div class="flex align-items-center gap-2">
                <p-checkbox
                    inputId="profileSubscriptionsExportIncludeInactive"
                    [(ngModel)]="profileSubscriptionsExportIncludeInactive"
                    [binary]="true" />
                <label for="profileSubscriptionsExportIncludeInactive">
                    Include inactive subscriptions
                </label>
            </div>
        </div>

        <p-message
            *ngIf="profileSubscriptionsExportError"
            severity="error"
            [text]="profileSubscriptionsExportError"
            styleClass="mt-2 w-full"></p-message>

        <div
            *ngIf="profileSubscriptionsExportResponse"
            class="subscriptions-summary mt-2">
            Top-level array length:
            <strong>{{ profileSubscriptionsExportProfileCount }}</strong>
            customer profile{{ profileSubscriptionsExportProfileCount === 1 ? '' : 's' }}
            · nested subscription details:
            <strong>{{ profileSubscriptionsExportSubscriptionCount }}</strong>
        </div>

        <div class="subscriptions-results mt-2">
            <label class="subscriptions-label">
                Export output
                <span *ngIf="profileSubscriptionsExportResponse as exportProfiles">
                    ({{ exportProfiles.length }}
                    {{ exportProfiles.length === 1 ? 'profile' : 'profiles' }})
                </span>
            </label>
            <pre
                *ngIf="profileSubscriptionsExportResponse; else noProfileSubscriptionsExportData"
                class="subscriptions-json">{{ profileSubscriptionsExportResponse | json }}</pre>
            <ng-template #noProfileSubscriptionsExportData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Click "Build all profile subscriptions JSON" to export every customer profile
                    and its subscriptions.
                </p>
            </ng-template>
        </div>

        <p-divider />

        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="subscriptionSearchType">Search type</label>
                <p-select
                    inputId="subscriptionSearchType"
                    [options]="searchTypeOptions"
                    [(ngModel)]="subscriptionParams.searchType"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    fluid />
            </div>
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="subscriptionOrderBy">Order by</label>
                <p-select
                    inputId="subscriptionOrderBy"
                    [options]="orderByOptions"
                    [(ngModel)]="subscriptionParams.orderBy"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    fluid />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="subscriptionLimit">Limit (1-1000)</label>
                <input
                    id="subscriptionLimit"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    max="1000"
                    [(ngModel)]="subscriptionParams.limit" />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="subscriptionOffset">Page (offset)</label>
                <input
                    id="subscriptionOffset"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    [(ngModel)]="subscriptionParams.offset" />
            </div>
            <div class="col-12 md:col-4 flex align-items-end">
                <div class="flex align-items-center gap-2 pb-2">
                    <p-checkbox
                        inputId="subscriptionOrderDescending"
                        [(ngModel)]="subscriptionParams.orderDescending"
                        [binary]="true" />
                    <label for="subscriptionOrderDescending">Sort descending</label>
                </div>
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-search"
                    label="Load subscriptions"
                    [loading]="subscriptionsLoading"
                    (click)="loadSubscriptions()"></button>
            </div>
        </div>

        <p-message *ngIf="subscriptionsError" severity="error" [text]="subscriptionsError" styleClass="mt-3 w-full"></p-message>

        <div *ngIf="subscriptionsResponse?.totalNumInResultSet != null" class="subscriptions-summary mt-2">
            Total in result set: {{ subscriptionsResponse.totalNumInResultSet }}
            <span *ngIf="subscriptionsResponse.subscriptionDetails?.length != null">
                (showing {{ subscriptionsResponse.subscriptionDetails.length }} on this page)
            </span>
        </div>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">List response</label>
            <pre *ngIf="subscriptionsResponse; else noSubscriptionData" class="subscriptions-json">{{ subscriptionsResponse | json }}</pre>
            <ng-template #noSubscriptionData>
                <p class="subscriptions-placeholder text-color-secondary">Click "Load subscriptions" to fetch the subscription list.</p>
            </ng-template>
        </div>

        <div
            *ngIf="subscriptionsResponse?.subscriptionDetails?.length"
            class="subscriptions-id-hints mt-2 text-sm text-color-secondary">
            <div>
                <span class="font-semibold">Subscription IDs: </span>
                <button
                    *ngFor="let sub of subscriptionsResponse.subscriptionDetails"
                    type="button"
                    pButton
                    class="p-button-sm p-button-outlined subscriptions-id-button"
                    [label]="'' + sub.id"
                    (click)="useSubscriptionIdFromList(sub.id)"></button>
            </div>
            <div class="mt-1">
                <span class="font-semibold">Customer profile IDs: </span>
                <ng-container *ngFor="let sub of subscriptionsResponse.subscriptionDetails">
                    <button
                        *ngIf="sub.customerProfileId"
                        type="button"
                        pButton
                        class="p-button-sm p-button-outlined subscriptions-id-button"
                        [label]="'' + sub.customerProfileId"
                        (click)="useCustomerProfileIdFromList(sub.customerProfileId)"></button>
                </ng-container>
            </div>
        </div>

        <div class="mt-3 flex flex-wrap align-items-center gap-3">
            <button
                pButton
                type="button"
                icon="pi pi-list"
                label="Load subscription details for list"
                [loading]="subscriptionDetailsListLoading"
                [disabled]="!subscriptionsResponse?.subscriptionDetails?.length"
                (click)="loadSubscriptionDetailsFromList()"></button>
            <div class="flex align-items-center gap-2">
                <p-checkbox
                    inputId="subscriptionDetailsListIncludeTransactions"
                    [(ngModel)]="subscriptionDetailsListIncludeTransactions"
                    [binary]="true" />
                <label for="subscriptionDetailsListIncludeTransactions">Include transactions</label>
            </div>
        </div>

        <p class="text-color-secondary text-sm mt-2 mb-0">
            Fetches full ARB subscription detail for each subscription on the current list page
            (includes <code>billTo.company</code>, profile IDs, payment schedule, etc.).
        </p>

        <p-message
            *ngIf="subscriptionDetailsListError"
            severity="error"
            [text]="subscriptionDetailsListError"
            styleClass="mt-3 w-full"></p-message>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Subscription details list</label>
            <pre
                *ngIf="subscriptionDetailsListResponse; else noSubscriptionDetailsListData"
                class="subscriptions-json">{{ subscriptionDetailsListResponse | json }}</pre>
            <ng-template #noSubscriptionDetailsListData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Load subscriptions, then click "Load subscription details for list".
                </p>
            </ng-template>
        </div>

        <div class="mt-3">
            <button
                pButton
                type="button"
                icon="pi pi-users"
                label="Verify customer profile summaries for list"
                [loading]="customerProfileSummariesLoading"
                [disabled]="!subscriptionsResponse?.subscriptionDetails?.length"
                (click)="loadCustomerProfileSummariesFromSubscriptions()"></button>
        </div>

        <p class="text-color-secondary text-sm mt-2 mb-0">
            Loads each subscription detail and customer profile, then compares profile
            <code>billTo</code> to subscription billing fields from detail
            <code>billTo</code> when present, otherwise subscription/list
            <code>firstName</code> and <code>lastName</code>.
            <code>company</code> and <code>address</code> are only checked when the subscription
            includes them.
        </p>

        <p-message
            *ngIf="customerProfileSummariesAllVerified === true"
            severity="success"
            [text]="customerProfileSummariesVerificationMessage"
            styleClass="mt-3 w-full"></p-message>

        <p-message
            *ngIf="customerProfileSummariesAllVerified === false"
            severity="warn"
            [text]="customerProfileSummariesVerificationMessage"
            styleClass="mt-3 w-full"></p-message>

        <p-message
            *ngIf="customerProfileSummariesError"
            severity="error"
            [text]="customerProfileSummariesError"
            styleClass="mt-3 w-full"></p-message>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Verified customer profile summaries</label>
            <pre
                *ngIf="customerProfileSummariesResponse; else noCustomerProfileSummariesData"
                class="subscriptions-json">{{ customerProfileSummariesResponse | json }}</pre>
            <ng-template #noCustomerProfileSummariesData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Load subscriptions, then click "Verify customer profile summaries for list".
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get subscription by ID</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Full subscription detail (includes billing company via <code>billTo.company</code>).
        </p>
        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="subscriptionDetailId">Subscription ID</label>
                <input
                    id="subscriptionDetailId"
                    type="text"
                    pInputText
                    class="w-full"
                    placeholder="e.g. 4818507"
                    [(ngModel)]="subscriptionDetailId" />
            </div>
            <div class="col-12 md:col-6 flex align-items-end">
                <div class="flex align-items-center gap-2 pb-2">
                    <p-checkbox
                        inputId="subscriptionDetailIncludeTransactions"
                        [(ngModel)]="subscriptionDetailIncludeTransactions"
                        [binary]="true" />
                    <label for="subscriptionDetailIncludeTransactions">Include transactions</label>
                </div>
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-id-card"
                    label="Get subscription"
                    [loading]="subscriptionDetailLoading"
                    [disabled]="!subscriptionDetailId?.trim()"
                    (click)="loadSubscriptionById()"></button>
            </div>
        </div>

        <p-message
            *ngIf="subscriptionDetailError"
            severity="error"
            [text]="subscriptionDetailError"
            styleClass="mt-3 w-full"></p-message>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Subscription detail response</label>
            <pre
                *ngIf="subscriptionDetailResponse; else noSubscriptionDetailData"
                class="subscriptions-json">{{ subscriptionDetailResponse | json }}</pre>
            <ng-template #noSubscriptionDetailData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Enter a subscription ID from the list above and click "Get subscription".
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get customer profile</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Loads CIM profile data (look for company on <code>profile.paymentProfiles[].billTo.company</code>).
        </p>
        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="customerProfileId">Customer profile ID</label>
                <input
                    id="customerProfileId"
                    type="text"
                    pInputText
                    class="w-full"
                    placeholder="e.g. 1812912918"
                    [(ngModel)]="customerProfileId" />
            </div>
            <div class="col-12 md:col-6 flex flex-column justify-content-end gap-2 pb-2">
                <div class="flex align-items-center gap-2">
                    <p-checkbox
                        inputId="customerProfileUnmaskExpirationDate"
                        [(ngModel)]="customerProfileUnmaskExpirationDate"
                        [binary]="true" />
                    <label for="customerProfileUnmaskExpirationDate">Unmask expiration date</label>
                </div>
                <div class="flex align-items-center gap-2">
                    <p-checkbox
                        inputId="customerProfileIncludeIssuerInfo"
                        [(ngModel)]="customerProfileIncludeIssuerInfo"
                        [binary]="true" />
                    <label for="customerProfileIncludeIssuerInfo">Include issuer info</label>
                </div>
            </div>
            <div class="col-12 flex gap-2 flex-wrap">
                <button
                    pButton
                    type="button"
                    icon="pi pi-user"
                    label="Get customer profile"
                    [loading]="customerProfileLoading"
                    [disabled]="!customerProfileId?.trim()"
                    (click)="loadCustomerProfile()"></button>
                <button
                    pButton
                    type="button"
                    icon="pi pi-copy"
                    label="Use profile from subscription detail"
                    class="p-button-outlined"
                    [disabled]="!subscriptionDetailResponse"
                    (click)="useCustomerProfileIdFromSubscriptionDetail()"></button>
            </div>
        </div>

        <p-message
            *ngIf="customerProfileError"
            severity="error"
            [text]="customerProfileError"
            styleClass="mt-3 w-full"></p-message>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Customer profile response</label>
            <pre
                *ngIf="customerProfileResponse; else noCustomerProfileData"
                class="subscriptions-json">{{ customerProfileResponse | json }}</pre>
            <ng-template #noCustomerProfileData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Enter a customer profile ID (from the list or subscription detail) and click "Get customer profile".
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get settled batch list</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Loads settled Authorize.Net batches. Click a batch ID to use it for transaction list.
        </p>
        <div class="grid">
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="firstSettlementDate">First settlement date</label>
                <input
                    id="firstSettlementDate"
                    type="date"
                    pInputText
                    class="w-full"
                    [(ngModel)]="settledBatchListParams.firstSettlementDate" />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="lastSettlementDate">Last settlement date</label>
                <input
                    id="lastSettlementDate"
                    type="date"
                    pInputText
                    class="w-full"
                    [(ngModel)]="settledBatchListParams.lastSettlementDate" />
            </div>
            <div class="col-12 md:col-4 flex align-items-end">
                <div class="flex align-items-center gap-2 pb-2">
                    <p-checkbox
                        inputId="settledBatchIncludeStatistics"
                        [(ngModel)]="settledBatchListParams.includeStatistics"
                        [binary]="true" />
                    <label for="settledBatchIncludeStatistics">Include statistics</label>
                </div>
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-calendar"
                    label="Get settled batch list"
                    [loading]="settledBatchListLoading"
                    (click)="loadSettledBatchList()"></button>
            </div>
        </div>

        <p-message
            *ngIf="settledBatchListError"
            severity="error"
            [text]="settledBatchListError"
            styleClass="mt-3 w-full"></p-message>

        <div
            *ngIf="settledBatchListResponse?.batchList?.length"
            class="subscriptions-id-hints mt-2 text-sm text-color-secondary">
            <span class="font-semibold">Batch IDs: </span>
            <ng-container *ngFor="let batch of settledBatchListResponse.batchList">
                <button
                    *ngIf="batch['batchId']"
                    type="button"
                    pButton
                    class="p-button-sm p-button-outlined subscriptions-id-button"
                    [label]="'' + batch['batchId']"
                    (click)="useBatchIdFromList(batch['batchId'])"></button>
            </ng-container>
        </div>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Settled batch list response</label>
            <pre
                *ngIf="settledBatchListResponse; else noSettledBatchListData"
                class="subscriptions-json">{{ settledBatchListResponse | json }}</pre>
            <ng-template #noSettledBatchListData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Click "Get settled batch list" to fetch settled batches.
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get unsettled transaction list</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Loads pending (not yet settled) Authorize.Net transactions. Click a transaction ID to use it for transaction details.
        </p>
        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="unsettledTransactionListOrderBy">Order by</label>
                <p-select
                    inputId="unsettledTransactionListOrderBy"
                    [options]="transactionListOrderByOptions"
                    [(ngModel)]="unsettledTransactionListParams.orderBy"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    fluid />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="unsettledTransactionListLimit">Limit</label>
                <input
                    id="unsettledTransactionListLimit"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    max="1000"
                    [(ngModel)]="unsettledTransactionListParams.limit" />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="unsettledTransactionListOffset">Page (offset)</label>
                <input
                    id="unsettledTransactionListOffset"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    [(ngModel)]="unsettledTransactionListParams.offset" />
            </div>
            <div class="col-12 md:col-4 flex align-items-end">
                <div class="flex align-items-center gap-2 pb-2">
                    <p-checkbox
                        inputId="unsettledTransactionListOrderDescending"
                        [(ngModel)]="unsettledTransactionListParams.orderDescending"
                        [binary]="true" />
                    <label for="unsettledTransactionListOrderDescending">Sort descending</label>
                </div>
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-clock"
                    label="Get unsettled transaction list"
                    [loading]="unsettledTransactionListLoading"
                    (click)="loadUnsettledTransactionList()"></button>
            </div>
        </div>

        <p-message
            *ngIf="unsettledTransactionListError"
            severity="error"
            [text]="unsettledTransactionListError"
            styleClass="mt-3 w-full"></p-message>

        <div *ngIf="unsettledTransactionListResponse?.totalNumInResultSet != null" class="subscriptions-summary mt-2">
            Total in result set: {{ unsettledTransactionListResponse.totalNumInResultSet }}
            <span *ngIf="unsettledTransactionListResponse.transactions?.length != null">
                (showing {{ unsettledTransactionListResponse.transactions.length }} on this page)
            </span>
        </div>

        <div
            *ngIf="unsettledTransactionListResponse?.transactions?.length"
            class="subscriptions-id-hints mt-2 text-sm text-color-secondary">
            <span class="font-semibold">Transaction IDs: </span>
            <ng-container *ngFor="let transaction of unsettledTransactionListResponse.transactions">
                <button
                    *ngIf="transaction['transId']"
                    type="button"
                    pButton
                    class="p-button-sm p-button-outlined subscriptions-id-button"
                    [label]="'' + transaction['transId']"
                    (click)="useTransactionIdFromList(transaction['transId'])"></button>
            </ng-container>
        </div>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Unsettled transaction list response</label>
            <pre
                *ngIf="unsettledTransactionListResponse; else noUnsettledTransactionListData"
                class="subscriptions-json">{{ unsettledTransactionListResponse | json }}</pre>
            <ng-template #noUnsettledTransactionListData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Click "Get unsettled transaction list" to fetch pending transactions.
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get transaction list</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Loads transactions for a settled Authorize.Net batch.
        </p>
        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="transactionListBatchId">Batch ID</label>
                <input
                    id="transactionListBatchId"
                    type="text"
                    pInputText
                    class="w-full"
                    placeholder="e.g. 6680535"
                    [(ngModel)]="transactionListParams.batchId" />
            </div>
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="transactionListOrderBy">Order by</label>
                <p-select
                    inputId="transactionListOrderBy"
                    [options]="transactionListOrderByOptions"
                    [(ngModel)]="transactionListParams.orderBy"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    fluid />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="transactionListLimit">Limit</label>
                <input
                    id="transactionListLimit"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    max="1000"
                    [(ngModel)]="transactionListParams.limit" />
            </div>
            <div class="col-12 md:col-4">
                <label class="subscriptions-label" for="transactionListOffset">Page (offset)</label>
                <input
                    id="transactionListOffset"
                    type="number"
                    pInputText
                    class="w-full"
                    min="1"
                    [(ngModel)]="transactionListParams.offset" />
            </div>
            <div class="col-12 md:col-4 flex align-items-end">
                <div class="flex align-items-center gap-2 pb-2">
                    <p-checkbox
                        inputId="transactionListOrderDescending"
                        [(ngModel)]="transactionListParams.orderDescending"
                        [binary]="true" />
                    <label for="transactionListOrderDescending">Sort descending</label>
                </div>
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-list"
                    label="Get transaction list"
                    [loading]="transactionListLoading"
                    [disabled]="!transactionListParams.batchId?.trim()"
                    (click)="loadTransactionList()"></button>
            </div>
        </div>

        <p-message
            *ngIf="transactionListError"
            severity="error"
            [text]="transactionListError"
            styleClass="mt-3 w-full"></p-message>

        <div *ngIf="transactionListResponse?.totalNumInResultSet != null" class="subscriptions-summary mt-2">
            Total in result set: {{ transactionListResponse.totalNumInResultSet }}
            <span *ngIf="transactionListResponse.transactions?.length != null">
                (showing {{ transactionListResponse.transactions.length }} on this page)
            </span>
        </div>

        <div
            *ngIf="transactionListResponse?.transactions?.length"
            class="subscriptions-id-hints mt-2 text-sm text-color-secondary">
            <span class="font-semibold">Transaction IDs: </span>
            <ng-container *ngFor="let transaction of transactionListResponse.transactions">
                <button
                    *ngIf="transaction['transId']"
                    type="button"
                    pButton
                    class="p-button-sm p-button-outlined subscriptions-id-button"
                    [label]="'' + transaction['transId']"
                    (click)="useTransactionIdFromList(transaction['transId'])"></button>
            </ng-container>
        </div>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Transaction list response</label>
            <pre
                *ngIf="transactionListResponse; else noTransactionListData"
                class="subscriptions-json">{{ transactionListResponse | json }}</pre>
            <ng-template #noTransactionListData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Enter a batch ID and click "Get transaction list".
                </p>
            </ng-template>
        </div>

        <p-divider />

        <h4 class="subscriptions-section-title">Get transaction details</h4>
        <p class="text-color-secondary text-sm mt-0 mb-3">
            Loads full details for an Authorize.Net transaction ID.
        </p>
        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="subscriptions-label" for="transactionDetailsTransId">Transaction ID</label>
                <input
                    id="transactionDetailsTransId"
                    type="text"
                    pInputText
                    class="w-full"
                    placeholder="e.g. 12345"
                    [(ngModel)]="transactionDetailsTransId" />
            </div>
            <div class="col-12 flex gap-2">
                <button
                    pButton
                    type="button"
                    icon="pi pi-search"
                    label="Get transaction details"
                    [loading]="transactionDetailsLoading"
                    [disabled]="!transactionDetailsTransId?.trim()"
                    (click)="loadTransactionDetails()"></button>
            </div>
        </div>

        <p-message
            *ngIf="transactionDetailsError"
            severity="error"
            [text]="transactionDetailsError"
            styleClass="mt-3 w-full"></p-message>

        <div class="subscriptions-results mt-3">
            <label class="subscriptions-label">Transaction details response</label>
            <pre
                *ngIf="transactionDetailsResponse; else noTransactionDetailsData"
                class="subscriptions-json">{{ transactionDetailsResponse | json }}</pre>
            <ng-template #noTransactionDetailsData>
                <p class="subscriptions-placeholder text-color-secondary">
                    Enter a transaction ID and click "Get transaction details".
                </p>
            </ng-template>
        </div>
    </div>
</p-dialog>
